import { useAttachmentGallery } from "@circle-react/components/Modals/AttachmentGalleryModal";
import type { AttachmentGalleryItem } from "@circle-react/components/Modals/AttachmentGalleryModal/types";
import {
  linkifyTrixContent,
  makeAllLinksOpenInNewWindow,
  setNoViewDownload,
  setupAttachmentDownload,
  transformDataBackgroundImage,
} from "@circle-react/helpers/actionTextHelpers";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { useIsIframeEmbed } from "@circle-react/hooks/useIsIframeEmbed";
import { useNavigate } from "@tanstack/react-router";
import InnerHTML from "dangerously-set-html-content";
import { useEffect, useMemo, useRef } from "react";

import {
  anchorClassNames,
  legacyClassNames,
} from "@/react/helpers/cssAnchorHelpers";
import { TrixContentAttachmentPreviewHelperService } from "@/src/TrixContentAttachmentPreviewHelperService";

import { useCompassTrixBlocks } from "./useCompassTrixBlocks";

interface TrixAttachment extends AttachmentGalleryItem {
  filesize: string | null;
  height: string | null;
  sgid: string | null;
  width: string | null;
}

interface OpenPreviewModalParams {
  attachments: TrixAttachment[];
  currentIndex: number;
}

interface DisplayTrixContentProps {
  content: string;
  contentParentPath?: string;
  disableFullScreenPreview?: boolean;
  transformLinks?: typeof makeAllLinksOpenInNewWindow;
}

export const DisplayTrixContent = ({
  content,
  disableFullScreenPreview = false,
  contentParentPath,
  transformLinks = makeAllLinksOpenInNewWindow,
}: DisplayTrixContentProps) => {
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);
  const { show: openAttachmentGallery } = useAttachmentGallery();
  const isIframeEmbed = useIsIframeEmbed();

  useEffect(() => {
    if (!ref.current) {
      return undefined;
    }

    const trixContentElement = ref.current.querySelector(".trix-content");
    if (!trixContentElement) {
      return undefined;
    }

    setNoViewDownload(trixContentElement);
    transformLinks({
      containerElement: trixContentElement,
      shouldIncludeInternalLinks: isIframeEmbed,
    });
    transformDataBackgroundImage(trixContentElement);
    setupAttachmentDownload(trixContentElement);

    const openPreviewModal = ({
      attachments,
      currentIndex,
    }: OpenPreviewModalParams) => {
      if (disableFullScreenPreview) {
        if (contentParentPath) {
          void navigate({ href: contentParentPath });
        }
        return;
      }

      void openAttachmentGallery({
        attachments,
        startIndex: currentIndex,
      });
    };

    const trixContentAttachmentPreviewHelperService =
      new TrixContentAttachmentPreviewHelperService(ref.current);
    return trixContentAttachmentPreviewHelperService.addAttachmentClickEventHandler(
      openPreviewModal,
    );
  }, [
    isIframeEmbed,
    contentParentPath,
    disableFullScreenPreview,
    navigate,
    openAttachmentGallery,
    transformLinks,
  ]);

  const formattedContent = useMemo(
    () => linkifyTrixContent(content),
    [content],
  );

  const compassBlockPortals = useCompassTrixBlocks(ref, formattedContent);

  return (
    <div
      className={classNames(
        anchorClassNames("react-trix-content trix-v2"),
        "text-dark",
        !disableFullScreenPreview &&
          legacyClassNames("fullscreen-preview-enabled"),
        !disableFullScreenPreview &&
          "[&_.fullscreen-previewable_.attachment__preview_link]:cursor-zoom-in [&_.fullscreen-previewable]:cursor-zoom-in",
      )}
      ref={ref}
    >
      <InnerHTML html={formattedContent} />
      {compassBlockPortals}
    </div>
  );
};
